import { PkIdentifier, PkAvatar, PkIcon, PkDivider } from '@uefadigital/panenka-react';
import clsx from 'clsx';

import { getStatByName } from '../../utils/misc';
import type { TopScorerPlayer } from '../../utils/types';
import LinkOrAnchor from '../LinkOrAnchor';

import { useWindowSize } from '@hooks/useWindowSize';
import TeamBadge from '@shared/TeamBadge';
import { useTranslateFn } from '@shared/Translate';
import GtmHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

export const BigCard = ({
  size,
  idCard,
  player,
  kind,
  imageUrl,
  grid,
  options,
}: {
  size: string;
  idCard: number;
  player: TopScorerPlayer;
  kind: string;
  imageUrl: string;
  grid: string;
  options: any;
}) => {
  const t = useTranslateFn();

  const goalsValue = getStatByName(player, 'goals');
  const assistsValue = getStatByName(player, 'assists');
  const minutesValue = getStatByName(player, 'minutes_played_official');
  const { isDesktop } = useWindowSize();

  const trackText = kind === 'video' ? 'Tap player video main' : 'Tap player image';
  const divStyle = {
    ...(imageUrl && { backgroundImage: `url(${imageUrl})` }),
  };

  const onClickEvent = () => {
    GtmHelper.event(
      'Click',
      `${trackText} - ${player?.player?.internationalName}`,
      'topscorer',
      { section_reference: 'topscorer', interaction: `${trackText} - ${player?.player?.internationalName}` },
      'topscorer',
    );
  };

  return (
    <LinkOrAnchor
      enableUclPlayerPage={options.enableUclPlayerPage}
      playerId={player.player.id}
      onClickEvent={onClickEvent}
    >
      <div
        style={divStyle}
        className={clsx(
          styles['ts-card--wide'],
          styles[`ts-card--${size}`],
          styles[`ts-card--${kind}`],
          styles[`id-card--${idCard}`],
          styles[`ts-card--${grid}`],
          'ts-card',
          `ts-card--${size}`,
          'ts-card--wide',
        )}
      >
        {kind === 'video' && <PkIcon className={styles.tsPlayIcon} name='av-play-circle' size={40} />}
        <div className={clsx(styles.tsRank, 'ts-rank')}>
          <p className={clsx(styles['ts-rank__label'], 'ts-rank__label')}>
            {`#${idCard} `}
            {idCard === 1 && t(`${options.isWomen ? 'women_' : ''}top_scorer`)}
          </p>
        </div>
        <div className={styles.tsPlayer}>
          {kind === 'placeholder' && (
            <PkIdentifier className={clsx(styles.tsPlayerIdentifier, 'pk-py--0')} type='horizontal' order='normal'>
              <span slot='prefix'>
                <PkAvatar
                  fallback-image='generic-head'
                  type='image'
                  size={isDesktop ? 60 : 40}
                  src={player.player.imageUrl}
                >
                  {player?.team && <TeamBadge slot='badge' team={player.team} size={isDesktop ? 22 : 14} />}
                </PkAvatar>
              </span>
              <span slot='primary' className={styles.tsPlayerName}>
                {player.player.name}
              </span>
              <span slot='secondary'>
                <span>{player?.team?.displayName}</span>
              </span>
            </PkIdentifier>
          )}
          {kind !== 'placeholder' && (
            <div className={styles.tsPlayerIdentifier}>
              <PkIdentifier className='pk-py--0' type='horizontal' order='inverted'>
                {player?.team && (
                  <div slot='secondary' className='pk-d--flex pk-gap--xs'>
                    <TeamBadge team={player.team} size={19} />
                    <span className='pk-text--text-01 pk-font-size--s'>{player?.team?.displayName}</span>
                  </div>
                )}
                <span slot='primary' className={styles.tsPlayerName}>
                  {player.player.name}
                </span>
              </PkIdentifier>
              {options?.enableEnhancedStatistics && (
                <div className={styles['ts-stats__enhanced']}>
                  {assistsValue > 0 && (
                    <div className={styles['ts-stats__stat']}>
                      <span className={styles.tsStatsAssists}>{assistsValue}</span>
                      <span className={styles.tsStatsLabel}>
                        {t(`${assistsValue === 1 ? 'uefa_assist' : 'stats_Assists'}`)}
                      </span>
                    </div>
                  )}
                  {!options.isFutsal && minutesValue > 0 && (
                    <div className={styles['ts-stats__stat']}>
                      <span className={styles.tsStatsMinutes}>{minutesValue}</span>
                      <span className={styles.tsStatsLabel}>{t('stats_minutes')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className={clsx(styles.tsDivider)}>
            <PkDivider direction='vertical' />
          </div>
          <div className={clsx(styles.tsStats)}>
            <div className={clsx(styles['ts-stats__stat'], styles['ts-stats__stat-goals'])}>
              <div className={clsx(styles.tsStatsGoals, 'ts-stats__goals')}>{goalsValue}</div>
              <div className={clsx(styles.tsStatsLabel, 'ts-stats__label')}>
                {t(`${goalsValue > 1 ? 'stats_goals' : 'stats_goal'}`)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </LinkOrAnchor>
  );
};

export default BigCard;
